import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract, faReceipt, faTrophy } from "@fortawesome/free-solid-svg-icons";
import styles from "./misRifasPage.module.css";
import { raffleService } from "../../../../../services/api";
import { useAuth } from "../../../../../context/authContext";
import { Badge, EmptyState, ErrorBanner, Spinner } from "../../../../../components/ui";
import { Reveal } from "../../../../../components/motion";
import RaffleReceiptModal from "../../../promociones/RaffleReceiptModal";
import {
  formatPromoDateTime,
  groupOutcome,
  safeExternalUrl,
  ticketOutcome,
  uniqueReceiptCodes,
} from "../../../promociones/promoUtils";

const FILTERS = [
  { id: "all", label: "Todas" },
  { id: "pending", label: "Pendientes" },
  { id: "won", label: "Ganadas" },
  { id: "lost", label: "No ganó" },
];

/**
 * "Mis rifas" en el perfil: GET /raffles/mine/ (boletas agrupadas por rifa).
 * Funciona aunque el módulo de promociones esté apagado.
 */
export default function MisRifasPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [reloadKey, setReloadKey] = useState(0);
  const [receipt, setReceipt] = useState(null); // { code, title }

  useEffect(() => {
    if (authLoading || !isAuthenticated) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    raffleService.mine().then((res) => {
      if (cancelled) return;
      setGroups(res.data.filter((g) => g && g.raffle));
      setError(res.ok ? "" : res.message);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Cargando tus rifas…" />
      </div>
    );
  }

  if (error) {
    return <ErrorBanner message={error} onRetry={reload} />;
  }

  const filtered = groups.filter((g) => {
    if (filter === "all") return true;
    const key = groupOutcome(g).key;
    if (filter === "pending") return key === "pending";
    return key === filter;
  });

  return (
    <div className={styles.container}>
      <div className={styles.head}>
        <h2 className={styles.title}>Mis rifas</h2>
        <Link to="/promociones" className={styles.headLink}>
          Ver promociones
        </Link>
      </div>

      {groups.length > 0 && (
        <div className={styles.filters} role="tablist" aria-label="Filtrar rifas">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              className={`${styles.filterBtn} ${filter === f.id ? styles.active : ""}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title={groups.length === 0 ? "Aún no participas en rifas" : "Sin resultados"}
          description={
            groups.length === 0
              ? "Cuando compres boletas de una rifa las verás aquí con tus números y comprobantes."
              : "No hay rifas con este estado."
          }
        />
      ) : (
        <ul className={styles.list}>
          {filtered.map((group) => {
            const raffle = group.raffle;
            const outcome = groupOutcome(group);
            const tickets = Array.isArray(group.tickets) ? group.tickets : [];
            const receipts = uniqueReceiptCodes(tickets);
            const termsUrl = safeExternalUrl(raffle.terms_url);
            return (
              <Reveal as="li" key={raffle.slug} className={styles.item}>
                <div className={styles.itemHead}>
                  {raffle.banner_image && (
                    <img src={raffle.banner_image} alt="" className={styles.thumb} loading="lazy" />
                  )}
                  <div className={styles.itemInfo}>
                    <Link
                      to={`/promociones/${encodeURIComponent(raffle.slug)}`}
                      className={styles.itemTitle}
                    >
                      {raffle.title}
                    </Link>
                    {raffle.prize_name && <p className={styles.itemMeta}>Premio: {raffle.prize_name}</p>}
                    {raffle.draw_at && (
                      <p className={styles.itemMeta}>Sorteo: {formatPromoDateTime(raffle.draw_at)}</p>
                    )}
                  </div>
                  <Badge variant={outcome.variant}>{outcome.label}</Badge>
                </div>

                {raffle.winning_number && (
                  <p className={styles.winning}>
                    <FontAwesomeIcon icon={faTrophy} /> Número ganador:{" "}
                    <strong>{raffle.winning_number}</strong>
                  </p>
                )}

                <p className={styles.ticketsLabel}>
                  {tickets.length === 1 ? "Tu boleta" : `Tus ${tickets.length} boletas`}
                </p>
                <ul className={styles.chips}>
                  {tickets.map((t) => {
                    const o = ticketOutcome(t, raffle);
                    return (
                      <li
                        key={`${t.number}-${t.receipt_code}`}
                        className={`${styles.chip} ${styles[`chip_${o.key}`] || ""}`}
                        title={o.label}
                      >
                        {t.number}
                        <span className={styles.srOnly}> ({o.label})</span>
                      </li>
                    );
                  })}
                </ul>

                <div className={styles.links}>
                  {receipts.map((code) => (
                    <button
                      key={code}
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => setReceipt({ code, title: raffle.title })}
                    >
                      <FontAwesomeIcon icon={faReceipt} /> Comprobante {code}
                    </button>
                  ))}
                  {termsUrl && (
                    <a
                      href={termsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.linkBtn}
                    >
                      <FontAwesomeIcon icon={faFileContract} /> Términos y condiciones
                    </a>
                  )}
                </div>
              </Reveal>
            );
          })}
        </ul>
      )}

      <RaffleReceiptModal
        isOpen={Boolean(receipt)}
        receiptCode={receipt?.code || null}
        raffleTitle={receipt?.title}
        onClose={() => setReceipt(null)}
      />
    </div>
  );
}
